
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Sparkles, MapPin, Calendar, ExternalLink } from 'lucide-react';
import { GOOGLE_COLORS } from '../constants';

interface ProjectItem {
  id: number;
  title: string;
  location: string;
  type: string;
  client: string;
}

const LOCATIONS = ['Ziggo Dome', 'Westergasfabriek', 'Euromast', 'Scheveningse Pier', 'Museumplein', 'Vondelpark', 'RAI Amsterdam', 'Spoorzone Tilburg'];
const PROJECT_TYPES = ['Internationaal Congres', 'Product Launch', 'Fashion Show', 'Outdoor Festival', 'Corporate Gala', 'VR Experience Center', 'E-Sports Toernooi'];
const CLIENTS = ['Global Tech Corp', 'Innovation Lab', 'Fashion House Paris', 'Ministry of Events', 'Energy Solutions', 'Creative Agency X'];

const ProjectsView: React.FC<{ onBack: () => void; onItemClick: (name: string) => void }> = ({ onBack, onItemClick }) => {
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [page, setPage] = useState(0);
  const loaderRef = useRef<HTMLDivElement>(null);

  const generateProjects = (count: number, startId: number) => {
    return Array.from({ length: count }).map((_, i) => ({
      id: startId + i,
      title: `${PROJECT_TYPES[Math.floor(Math.random() * PROJECT_TYPES.length)]} ${2024 + Math.floor(Math.random() * 2)}`,
      location: LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)],
      type: PROJECT_TYPES[Math.floor(Math.random() * PROJECT_TYPES.length)],
      client: CLIENTS[Math.floor(Math.random() * CLIENTS.length)]
    }));
  };

  useEffect(() => {
    setProjects(generateProjects(10, 0));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (page > 0) {
      const newProjects = generateProjects(5, projects.length);
      setProjects((prev) => [...prev, ...newProjects]);
    }
  }, [page]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="border-b border-gray-100 sticky top-0 z-10 bg-white/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center text-gray-500 hover:text-black transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span>Terug</span>
          </button>
          <h2 className="text-2xl font-bold tracking-tight">
            <span style={{ color: GOOGLE_COLORS.blue }}>P</span>
            <span style={{ color: GOOGLE_COLORS.red }}>r</span>
            <span style={{ color: GOOGLE_COLORS.yellow }}>o</span>
            <span style={{ color: GOOGLE_COLORS.blue }}>j</span>
            <span style={{ color: GOOGLE_COLORS.green }}>e</span>
            <span style={{ color: GOOGLE_COLORS.red }}>c</span>
            <span style={{ color: GOOGLE_COLORS.yellow }}>t</span>
            <span style={{ color: GOOGLE_COLORS.blue }}>e</span>
            <span style={{ color: GOOGLE_COLORS.green }}>n</span>
          </h2>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 w-full flex flex-col space-y-12 py-12">
        {projects.map((project) => (
          <div 
            key={project.id} 
            className="group cursor-pointer hover:bg-gray-50 p-6 -mx-6 rounded-3xl transition-all active:scale-[0.98]"
            onClick={() => onItemClick(project.title)}
          >
            <div className="flex items-start space-x-6">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shrink-0 shadow-lg group-hover:rotate-12 transition-transform">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div className="flex-grow">
                <div className="flex items-center space-x-2 mb-1">
                    <span className="text-blue-600 text-sm font-bold uppercase tracking-widest">{project.type}</span>
                    <span className="text-gray-300">•</span>
                    <span className="text-gray-500 text-sm">{project.client}</span>
                </div>
                <div className="flex items-center justify-between">
                  <h3 className="text-3xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{project.title}</h3>
                  <ExternalLink className="w-6 h-6 text-gray-300 group-hover:text-blue-500 transition-colors" />
                </div>
                <div className="flex items-center space-x-4 mt-3 text-gray-500">
                    <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1 text-red-500" />
                        <span>{project.location}</span>
                    </div>
                    <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1 text-green-600" />
                        <span>Q{Math.floor(Math.random() * 4) + 1} 2024</span>
                    </div>
                </div>
              </div>
            </div>
            <div className="mt-8 h-[1px] bg-gray-100 w-full group-last:hidden"></div>
          </div>
        ))}

        <div ref={loaderRef} className="py-20 flex flex-col items-center">
          <p className="text-gray-400 text-sm animate-pulse mb-4">Onze portfolio breidt zich uit...</p>
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-[#4285F4] rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-[#EA4335] rounded-full animate-bounce [animation-delay:-.3s]"></div>
            <div className="w-2 h-2 bg-[#FBBC05] rounded-full animate-bounce [animation-delay:-.5s]"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsView;
